// @flow

import React, { useEffect } from 'react';
import { parse } from 'query-string';
import prop from 'lodash/fp/prop';
import flow from 'lodash/fp/flow';
import toUpper from 'lodash/fp/toUpper';
import connect from '@active/awe-ui-core/lib/hoc/connect';
import Submit from 'components/VirtualEvent/SubmitResult/Submit';
import Result from 'components/VirtualEvent/SubmitResult/Result';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import MaskSpinner from 'components/MaskSpinner/MaskSpinner';
import injectL10n from 'react-aaui/lib/shared/injectL10n';
import L10nMessage from 'react-aaui/lib/shared/L10nMessage';
import type { Location } from 'react-router-dom';

import type {
  L10n,
  ParticipantSearchResult,
  SubmitVirtualEventResultPayload,
  VirtualEventStageResult,
} from 'type/type';

import {
  getEvent,
  getParticipantSearchResult,
  isSearchingParticipantByNumber,
  isSearchParticipantByNumberFailed,
  isSubmittingVirtualEventResult,
  isVirtualEventResultSubmitted,
} from '../duck/selector';

import {
  submitVirtualEventResult as submitVirtualEventResultAction,
  searchParticipantByNumber as searchParticipantByNumberAction,
} from '../duck/duck';

import './ResubmitResult.scss';

type ResultData = {
  stageResults: Array<VirtualEventStageResult>,
  fileId: string
}

type SearchParticipantByNumberPayload = {
  eventId: string,
  registrationNumber: string,
  resubmit: boolean,
}

export type Props = {
  eventId: string,
  noEvent: boolean,
  isLoadingParticipantInfo: boolean,
  isLoadParticipantInfoFailed: boolean,
  isSubmittingResult: boolean,
  isResultSubmitted: boolean,
  participantInfo: ?ParticipantSearchResult,
  searchParticipantByNumber: (payload: SearchParticipantByNumberPayload) => void,
  submitVirtualEventResult: (payload: SubmitVirtualEventResultPayload) => void,
  l10n: L10n,
  location: Location,
}

export const ResubmitResult = ({
  eventId,
  noEvent,
  isLoadingParticipantInfo,
  isLoadParticipantInfoFailed,
  isSubmittingResult,
  isResultSubmitted,
  participantInfo,
  searchParticipantByNumber,
  submitVirtualEventResult,
  l10n: { formatMessage },
  location,
}: Props) => {
  useEffect(() => {
    const { registrationNumber } = flow(prop('search'), parse)(location);
    if (!noEvent) {
      searchParticipantByNumber({ eventId, registrationNumber, resubmit: true });
    }
  }, []);

  if (noEvent || isLoadParticipantInfoFailed) {
    return (
      <div className="event-resubmit-result__error">
        <span className="event-resubmit-result__error-message">
          <L10nMessage id="app.error.message.GENERAL" />
        </span>
      </div>
    );
  }

  if (isLoadingParticipantInfo) {
    return <PageSpinner />;
  }

  if (isResultSubmitted && participantInfo) {
    const sportType = flow(prop('sportType'), toUpper)(participantInfo);
    const sportTypeName = formatMessage(`global.sportsType.${sportType}`);
    const distanceName = prop('distanceName')(participantInfo);

    return <Result category={`${sportTypeName} - ${distanceName}`} />;
  }

  return (
    <div>
      { isSubmittingResult && <MaskSpinner /> }
      { participantInfo && <Submit
        participantInfo={participantInfo}
        onSubmitResult={({ stageResults, fileId }: ResultData) => {
          submitVirtualEventResult({
            eventId,
            stageResults,
            uploadFileId: fileId,
            resubmit: true,
            externalRouteId: prop('externalRouteId')(participantInfo),
            registrationNumber: prop('registrationNumber')(participantInfo),
          });
        }} />}
    </div>
  );
};

export default injectL10n()(
  connect(
    (state) => {
      const event = getEvent(state);
      return event ? {
        noEvent: false,
        eventId: event.toJSON().id,
        participantInfo: getParticipantSearchResult(state),
        isLoadingParticipantInfo: isSearchingParticipantByNumber(state),
        isLoadParticipantInfoFailed: isSearchParticipantByNumberFailed(state),
        isSubmittingResult: isSubmittingVirtualEventResult(state),
        isResultSubmitted: isVirtualEventResultSubmitted(state),
      } : { noEvent: true };
    },
    {
      searchParticipantByNumber: searchParticipantByNumberAction,
      submitVirtualEventResult: submitVirtualEventResultAction,
    },
  )(ResubmitResult),
);
